import { useTranslation } from 'react-i18next';
import { ActionConfigForm } from './action-config';
import { Form, MostActionConfigJobFormProps } from './types';
import { DocumentNode, useMutation } from '@apollo/client';
import { Dayjs } from 'dayjs';
import {
  CreateMostScheduledJobDocument,
  UpdateMostScheduledJobDocument,
} from 'src/graphql/generated/graphql';
import { useMostScheduledJob } from 'src/hooks/query/most/use-most-scheduled-job';
import { getCron } from 'src/components/most/list/scheduled-jobs/utils/cron';
import { PartialMostScheduledJobListType } from 'src/components/most/list/scheduled-jobs/types';

type MostScheduledJobFormProps = Omit<
  MostActionConfigJobFormProps,
  'onSubmit'
> & {
  id?: string;
  refetchQueries?: DocumentNode[];
  isClone?: boolean;
  onFinish: (id?: string) => void;
};

export const getJob = (form: Form) => {
  return {
    commandId: form.jobConfig?.command.commandId,
    description: form.jobConfig?.command.description,
    name: form.jobConfig?.command.name,
    type: form.jobConfig?.command.type,
    devices: form.jobConfig?.devices,
    storeIds: form.jobConfig?.storeIds,
  };
};

const getSchedule = (form: Form) => {
  const date = form.schedule.cron.date as Dayjs;
  const time = form.schedule.cron.time as Dayjs;

  const entity: { nextRunAt: string; cron?: string } = {
    nextRunAt: date.hour(time.hour()).minute(time.minute()).toISOString(),
  };

  if (form.schedule.cron.recurring) {
    entity.cron = getCron(form.schedule.cron);
  }
  return entity;
};

export const ScheduledJobForm = ({
  id,
  refetchQueries,
  isClone,
  onFinish,
  loading: loadingProp,
  ...rest
}: MostScheduledJobFormProps) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'most.list.schedule.form',
  });

  const [createEntity, { loading: loadingCreate }] = useMutation(
    CreateMostScheduledJobDocument,
    {
      refetchQueries,
    },
  );
  const [updateEntity, { loading: loadingUpdate }] = useMutation(
    UpdateMostScheduledJobDocument,
    {
      refetchQueries,
    },
  );

  const loading = loadingCreate || loadingUpdate || loadingProp;

  const { entity } = useMostScheduledJob({ id });

  const onSubmit = async (form: Form) => {
    const data = {
      jobConfig: getJob(form),
      schedule: getSchedule(form),
    };

    if (!entity?.id || isClone) {
      const result = await createEntity({
        variables: { data },
      });
      onFinish?.(result.data?.createMostScheduledJob.id);
    } else {
      const result = await updateEntity({
        variables: {
          data,
          id: entity.id,
        },
      });
      onFinish?.(result.data?.updateMostScheduledJob.id);
    }
  };

  return (
    <ActionConfigForm
      entity={entity as unknown as PartialMostScheduledJobListType}
      onSubmit={onSubmit}
      loading={loading}
      submitButtonLabel={t('submit')}
      {...rest}
    />
  );
};
